import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

interface CatalogChild {
  id: number;
  sku: string | null;
  name: string | null;
  image_file: string | null;
  kind: string | null;
  quantity: number;
  stock: number | null;
}

interface CatalogItem {
  id: number;
  sku: string;
  name: string;
  image_file: string | null;
  kind: string;
  product_group: string | null;
  stock: number | null;
  children?: CatalogChild[];
}

interface CatalogGame {
  gameId: number;
  game: string;
  products: CatalogItem[];
  components: CatalogItem[];
}

interface CatalogResponse {
  games: CatalogGame[];
  productCount: number;
  componentCount: number;
}

const KIND_LABELS: Record<string, string> = {
  printed: 'Nyomtatott',
  bought: 'Vásárolt',
  assembled: 'Összeépített',
};

/**
 * BGFox termékek — 2026-10-09 óta csökkentett üzemmód: a BGFox játékok
 * termékeiről és komponenseiről nem vezetünk készletet, nincs hiány, gyártási
 * vagy beszerzési cél. Az adatok itt, csak olvashatóan látszanak; a készlet az
 * utolsó ismert, tájékoztató érték.
 */
@Component({
  selector: 'app-bgfox-termekek',
  templateUrl: './bgfox-termekek.component.html',
  styleUrl: './bgfox-termekek.component.scss',
})
export class BgfoxTermekekComponent implements OnInit {
  loading = false;
  loadFailed = false;
  search = '';
  games: CatalogGame[] = [];
  productCount = 0;
  componentCount = 0;

  private openGames = new Set<number>();
  private openProducts = new Set<number>();
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.load();
  }

  onSearch(value: string): void {
    this.search = value;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.load(), 300);
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    const search = this.search.trim();
    const params: Record<string, string> = search ? { search } : {};
    this.http.get<CatalogResponse>('components/untracked-catalog', { params }).subscribe({
      next: (res) => {
        this.games = res?.games || [];
        this.productCount = res?.productCount || 0;
        this.componentCount = res?.componentCount || 0;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  /** Keresésnél minden találat nyitva van, egyébként a játékok csukva indulnak. */
  isGameOpen(game: CatalogGame): boolean {
    return !!this.search.trim() || this.openGames.has(game.gameId);
  }

  toggleGame(game: CatalogGame): void {
    if (this.openGames.has(game.gameId)) {
      this.openGames.delete(game.gameId);
    } else {
      this.openGames.add(game.gameId);
    }
  }

  isProductOpen(product: CatalogItem): boolean {
    return this.openProducts.has(product.id);
  }

  toggleProduct(product: CatalogItem): void {
    if (!product.children?.length) {
      return;
    }
    if (this.openProducts.has(product.id)) {
      this.openProducts.delete(product.id);
    } else {
      this.openProducts.add(product.id);
    }
  }

  kindLabel(kind: string | null): string {
    return (kind && KIND_LABELS[kind]) || '';
  }

  stockLabel(stock: number | null): string {
    return stock === null || stock === undefined ? '–' : `${stock} db`;
  }

  trackGame = (_: number, game: CatalogGame) => game.gameId;
  trackItem = (_: number, item: { id: number }) => item.id;
}
